import axios from 'axios';

// SEC/joblex: относительный base → same-origin на любом домене (rabotamax.ru/admin
// И admin.joblex.ru/admin). Раньше захардкоженный https://rabotamax.ru/api делал
// вызовы cross-origin с поддомена joblex → CORS-блок.
const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      // joblex: SPA смонтирована в корне поддомена (admin.joblex.ru), basename "/".
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Survey API methods
export const surveyAPI = {
  getSurvey: async (surveyId: number) => {
    const response = await api.get(`/surveys/${surveyId}`);
    return response.data;
  },
  getSurveyResponses: async (surveyId: number) => {
    const response = await api.get(`/surveys/${surveyId}/responses`);
    return response.data;
  },
  duplicateSurvey: async (surveyId: number) => {
    const response = await api.post(`/surveys/${surveyId}/duplicate`);
    return response.data;
  }
};

export type BlogArticleStatus = 'draft' | 'scheduled' | 'published';

export interface BlogArticle {
  id?: number;
  slug: string;
  title: string;
  description?: string | null;
  body: string;
  bodyHtml?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  authorName?: string | null;
  publishedAt?: string | null;
  /** ISO UTC; обязательно при status = 'scheduled'. */
  scheduledPublishAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  viewsCount?: number;
  likesCount?: number;
  readingTimeMinutes?: number;
  status: BlogArticleStatus;
  tags?: string[];
  /** Рубрика профессии (/blog/<hubSlug>/<slug>); null — без рубрики. */
  hubSlug?: string | null;
}

/** PUT /blog/admin/articles/:id — оверлей: непереданные поля сохраняются, null очищает. */
export type BlogArticleUpdate = Partial<Omit<BlogArticle, 'id' | 'createdAt' | 'updatedAt' | 'viewsCount' | 'likesCount' | 'bodyHtml'>>;

export interface BlogImage {
  /** root-relative /blog/img/<key>.webp — именно это пишем в тело статьи. */
  url: string;
  key: string;
  width: number;
  height: number;
  bytes: number;
  contentType: string;
}

export const BLOG_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const BLOG_IMAGE_MAX_BYTES = 8 * 1024 * 1024;

/** Тело ошибки blog/admin: {error, problems?} — problems = ошибки формата guide-тела. */
export interface BlogApiErrorBody {
  error?: string;
  problems?: string[];
}

export function blogApiError(e: unknown, fallback: string): { message: string; problems: string[] } {
  const data = axios.isAxiosError(e) ? (e.response?.data as BlogApiErrorBody | undefined) : undefined;
  const problems = Array.isArray(data?.problems) ? data!.problems.filter((p) => typeof p === 'string') : [];
  const message = typeof data?.error === 'string' && data.error ? data.error : fallback;
  return { message, problems };
}

export interface BlogHub {
  slug: string;
  label: string;
}

export const blogAPI = {
  /** Реестр рубрик блога (источник — blog-go categories). */
  categories: async (): Promise<{ categories: BlogHub[] }> => {
    const response = await api.get('/blog/admin/categories');
    return response.data;
  },
  list: async (): Promise<{ articles: BlogArticle[] }> => {
    const response = await api.get('/blog/admin/articles');
    return response.data;
  },
  get: async (id: number): Promise<{ article: BlogArticle }> => {
    const response = await api.get(`/blog/admin/articles/${id}`);
    return response.data;
  },
  create: async (data: Partial<BlogArticle>): Promise<{ article: BlogArticle }> => {
    const response = await api.post('/blog/admin/articles', data);
    return response.data;
  },
  update: async (id: number, data: BlogArticleUpdate): Promise<{ article: BlogArticle }> => {
    const response = await api.put(`/blog/admin/articles/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/blog/admin/articles/${id}`);
  },
  /** multipart, поле file: jpeg/png/webp/gif ≤ 8 МБ → 201 BlogImage. */
  uploadImage: async (file: File, onProgress?: (pct: number) => void): Promise<BlogImage> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/blog/admin/images', form, {
      // Не application/json инстанса (axios 1.x иначе сериализует FormData в JSON);
      // boundary к multipart/form-data браузер допишет сам.
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (ev) => {
        if (onProgress && ev.total) onProgress(Math.round((ev.loaded / ev.total) * 100));
      },
    });
    return response.data;
  },
  /** Сервер сам скачивает картинку по https-ссылке и кладёт к себе. */
  importImage: async (url: string): Promise<BlogImage> => {
    const response = await api.post('/blog/admin/images/import', { url });
    return response.data;
  }
};

export interface AdminResumeListItem {
  id: number;
  userId: number;
  title: string;
  projectId: number | null;
  userEmail: string;
  userFullName: string;
  source: 'consultant' | 'my_resumes' | 'file_upload';
  sourceLabel: string;
  originalFileName?: string | null;
  originalFileMime?: string | null;
  originalFileSize?: number | null;
  hasOriginalFile?: boolean;
  createdAt: string;
  updatedAt: string;
  lastModified: string;
  lastActivityAt: string;
  fillPercent: number;
  blocks: { key: string; label: string; filled: boolean }[];
}

// --- Наставник: диалоги с Олег/Анна/Советы ИИ (заменяет старый consultant-projects) ---

export type MentorPersona = 'anna' | 'oleg' | 'ai_advice';

export interface MentorDialogListItem {
  conversationId: string;
  persona: MentorPersona | string;
  personaLabel: string;
  userId: number | null;
  userEmail: string;
  userFullName: string;
  turns: number;
  firstMessage: string;
  startedAt: string;
  lastActivityAt: string;
}

export interface MentorDialogTurn {
  id: number;
  userMessage: string;
  responseText: string;
  model: string | null;
  createdAt: string;
}

export const mentorDialogsAPI = {
  list: async (persona = '', page = 1, limit = 100): Promise<{
    success: boolean;
    dialogs: MentorDialogListItem[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> => {
    const response = await api.get('/admin/mentor-dialogs', { params: { persona: persona || undefined, page, limit } });
    return response.data;
  },
  getConversation: async (conversationId: string, persona = ''): Promise<{
    success: boolean;
    persona: string;
    personaLabel: string;
    turns: MentorDialogTurn[];
  }> => {
    const response = await api.get(`/admin/mentor-dialogs/${encodeURIComponent(conversationId)}`, { params: { persona: persona || undefined } });
    return response.data;
  },
};

export const resumesAdminAPI = {
  list: async (page = 1, limit = 100): Promise<{
    success: boolean;
    resumes: AdminResumeListItem[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> => {
    const response = await api.get('/admin/resumes', { params: { page, limit } });
    return response.data;
  },
  get: async (id: number): Promise<{
    success: boolean;
    resume: Record<string, unknown>;
    meta: {
      userEmail: string;
      userFullName: string;
      fillPercent: number;
      blocks: { key: string; label: string; filled: boolean }[];
    };
  }> => {
    const response = await api.get(`/admin/resumes/${id}`);
    return response.data;
  },
  // downloadOriginal — скачивает оригинальный загруженный файл (стрим из admin-go
  // под Bearer-auth; S3-endpoint внутренний, поэтому не presigned-ссылка, а blob).
  downloadOriginal: async (id: number, filename?: string): Promise<void> => {
    const response = await api.get(`/admin/resumes/${id}/original`, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || `resume-${id}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  },
};

export interface AdminPromoCode {
  id: number;
  code: string;
  subscriptionType: string;
  discountKind: string;
  discountValue: number;
  maxUses?: number | null;
  usesCount: number;
  appliedCount: number;
  validFrom?: string | null;
  validUntil?: string | null;
  restrictedUserId?: number | null;
  allowsMultiplePayments: boolean;
  isActive: boolean;
  createdByAdminId?: number | null;
  createdAt: string;
  updatedAt: string;
}

export const promoCodesAPI = {
  list: async (limit = 50, offset = 0): Promise<{ success: boolean; items?: AdminPromoCode[]; total?: number }> => {
    const response = await api.get('/admin/promo-codes', { params: { limit, offset } });
    return response.data;
  },
  get: async (id: number): Promise<{ success: boolean; promo?: AdminPromoCode; message?: string }> => {
    const response = await api.get(`/admin/promo-codes/${id}`);
    return response.data;
  },
  create: async (body: Record<string, unknown>): Promise<{ success: boolean; id?: number; message?: string }> => {
    const response = await api.post('/admin/promo-codes', body);
    return response.data;
  },
  patch: async (id: number, body: Record<string, unknown>): Promise<{ success: boolean; message?: string }> => {
    const response = await api.patch(`/admin/promo-codes/${id}`, body);
    return response.data;
  },
  delete: async (id: number): Promise<{ success: boolean; message?: string }> => {
    const response = await api.delete(`/admin/promo-codes/${id}`);
    return response.data;
  },
  assign: async (id: number, userId: number): Promise<{ success: boolean; assignmentId?: number; message?: string }> => {
    const response = await api.post(`/admin/promo-codes/${id}/assign`, { userId });
    return response.data;
  },
  events: async (id: number, limit = 100, offset = 0): Promise<{
    success: boolean;
    events?: Array<{
      id: number;
      eventType: string;
      promoCodeId: number;
      userId?: number;
      adminId?: number;
      paymentId?: number;
      payload?: Record<string, unknown>;
      createdAt: string;
    }>;
    total?: number;
  }> => {
    const response = await api.get(`/admin/promo-codes/${id}/events`, { params: { limit, offset } });
    return response.data;
  },
  assignments: async (
    id: number,
    limit = 100,
    offset = 0
  ): Promise<{
    success: boolean;
    items?: Array<{
      id: number;
      userId: number;
      promoCodeId: number;
      status: string;
      assignedAt: string;
      assignedByAdminId?: number | null;
      consumedAt?: string | null;
    }>;
    total?: number;
    message?: string;
  }> => {
    const response = await api.get(`/admin/promo-codes/${id}/assignments`, { params: { limit, offset } });
    return response.data;
  },
  revokeAssignment: async (
    promoId: number,
    assignmentId: number
  ): Promise<{ success: boolean; message?: string }> => {
    const response = await api.post(`/admin/promo-codes/${promoId}/assignments/${assignmentId}/revoke`, {});
    return response.data;
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Admin: Анализ зарплаты — заполненные опросы salary form + AI usage info.
// Источник: /api/admin/salary-analysis в admin-go.
// ─────────────────────────────────────────────────────────────────────────────
export interface SalaryAnalysisItem {
  id: number;
  userId: number;
  userEmail: string;
  userFullName: string;
  role: string;
  city: string;
  experience: string;
  level: string;
  format: string;
  currentSalary: number;
  createdAt: string;
  aiModel?: string | null;
  aiTokens?: number | null;
  aiCreatedAt?: string | null;
}

export const salaryAnalysisAPI = {
  list: async (page = 1, limit = 50): Promise<{
    success: boolean;
    items: SalaryAnalysisItem[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> => {
    const response = await api.get('/admin/salary-analysis', { params: { page, limit } });
    return response.data;
  },
  get: async (id: number): Promise<{ success: boolean; item: SalaryAnalysisItem }> => {
    const response = await api.get(`/admin/salary-analysis/${id}`);
    return response.data;
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Admin: Гипотезы и цели — funnel-аналитика онбординга.
// ─────────────────────────────────────────────────────────────────────────────
export interface HypothesesFunnelStep {
  key: string;
  label: string;
  count: number;
}
export interface HypothesesFunnelDistribution {
  hypothesesPerUser: number;
  users: number;
}
export interface HypothesesFunnelAggregate {
  steps: HypothesesFunnelStep[];
  hypothesesDistribution: HypothesesFunnelDistribution[];
  avgSalaryTarget: number;
  usersTotal: number;
}
export interface HypothesesFunnelUser {
  userId: number;
  email: string;
  fullName: string;
  hasResume: boolean;
  hypothesesCount: number;
  hasGoal: boolean;
  salaryTarget: number;
  pipelineCount: number;
  lastActivity?: string | null;
}
export interface HypothesesFunnelUserDetail {
  userId: number;
  email?: string | null;
  fullName: string;
  salarySubmission?: {
    role: string;
    city: string;
    experience: string;
    level: string;
    format: string;
    currentSalary: number;
    createdAt: string;
  } | null;
  hypotheses: Array<{
    id: number;
    role: string;
    domain: string;
    industries: string;
    matchPercent: number;
    tag: string;
    isPrimary: boolean;
    createdAt: string;
  }>;
  goal?: {
    companyType: string;
    targetRole: string;
    industry: string;
    salaryTarget: number;
    workFormat: string;
    city: string;
    createdAt: string;
    updatedAt: string;
  } | null;
  pipeline: Array<{ vacancyId: number; stage: string; movedAt: string }>;
  resumes: Array<{ id: number; title: string; updatedAt: string }>;
}

export const hypothesesFunnelAPI = {
  aggregate: async (): Promise<{ success: boolean; aggregate: HypothesesFunnelAggregate }> => {
    const response = await api.get('/admin/hypotheses-funnel/aggregate');
    return response.data;
  },
  listUsers: async (page = 1, limit = 50): Promise<{
    success: boolean;
    items: HypothesesFunnelUser[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> => {
    const response = await api.get('/admin/hypotheses-funnel/users', { params: { page, limit } });
    return response.data;
  },
  getUserDetail: async (userId: number): Promise<{ success: boolean; user: HypothesesFunnelUserDetail }> => {
    const response = await api.get(`/admin/hypotheses-funnel/users/${userId}`);
    return response.data;
  },
  getUserFeed: async (
    userId: number,
    hypothesisId?: number,
    offset = 0,
    limit = 30,
  ): Promise<{
    success: boolean;
    hasCriteria: boolean;
    activeHypothesisRole?: string;
    cached?: boolean;
    vacancies: FunnelFeedVacancy[];
    total: number;
    message?: string;
  }> => {
    const params: Record<string, number> = { limit, offset };
    if (hypothesisId) params.hypothesisId = hypothesisId;
    const response = await api.get(`/admin/hypotheses-funnel/users/${userId}/feed`, { params });
    return response.data;
  },
};

export interface FunnelFeedVacancy {
  id: number;
  title: string;
  company: string;
  location: string;
  source: string;
  matchPercent: number;
  salary?: { from?: number; to?: number; currency?: string } | null;
  parsedAt: string;
  vacancyUrl: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin: Support Tickets — обращения пользователей в поддержку.
// admin-go проксирует в core-go /api/internal/support/* с X-Internal-Secret.
// ─────────────────────────────────────────────────────────────────────────────
export type SupportTicketStatus = 'open' | 'answered' | 'user_replied' | 'closed';
export type SupportTicketPriority = 'low' | 'normal' | 'high';

export interface AdminSupportTicket {
  id: number;
  userId: number;
  subject: string;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  lastMessageAt: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string | null;
  messageCount?: number;
  userEmail?: string;
  userName?: string;
}

export interface AdminSupportTicketMessage {
  id: number;
  ticketId: number;
  authorRole: 'user' | 'admin' | 'system';
  authorUserId?: number | null;
  body: string;
  createdAt: string;
}

export const adminSupportAPI = {
  listTickets: async (status?: SupportTicketStatus | ''): Promise<{
    success: boolean;
    tickets: AdminSupportTicket[];
  }> => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    const r = await api.get('/admin/support/tickets', { params });
    return r.data;
  },
  getTicket: async (id: number): Promise<{
    success: boolean;
    ticket: AdminSupportTicket;
    messages: AdminSupportTicketMessage[];
  }> => {
    const r = await api.get(`/admin/support/tickets/${id}`);
    return r.data;
  },
  addMessage: async (
    id: number,
    body: string,
    closeAfter?: boolean,
  ): Promise<{ success: boolean; message?: string }> => {
    const payload: Record<string, unknown> = { body };
    if (closeAfter) payload.closeAfter = true;
    const r = await api.post(`/admin/support/tickets/${id}/messages`, payload);
    return r.data;
  },
  patchTicket: async (
    id: number,
    patch: { status?: SupportTicketStatus; priority?: SupportTicketPriority },
  ): Promise<{ success: boolean; message?: string }> => {
    const r = await api.patch(`/admin/support/tickets/${id}`, patch);
    return r.data;
  },
  // draftReply — сгенерировать ЧЕРНОВИК ответа через LLM (с контекстом тикета).
  // Ничего не отправляет пользователю: возвращает текст для правки в форме ответа.
  draftReply: async (
    id: number,
  ): Promise<{ success: boolean; draft?: string; message?: string }> => {
    const r = await api.post(`/admin/support/tickets/${id}/draft-reply`);
    return r.data;
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Admin: Зарплатный рынок — rollup-аналитика (percentiles) по bucket'ам.
// Источник: /api/admin/salary-market в admin-go.
// Privacy: bucket'ы с n < minSamples не возвращаются бэкендом.
// ─────────────────────────────────────────────────────────────────────────────
export interface SalaryMarketBucket {
  bucket: {
    role: string;
    roleCanonical: string;
    city: string;
    experience: string;
  };
  n: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  avg: number;
  stddev: number;
}

export interface SalaryMarketFilters {
  role?: string;
  city?: string;
  experience?: string;
  source?: string;
  dateFrom?: string;
  dateTo?: string;
  minSamples?: number;
}

export const salaryMarketAPI = {
  list: async (filters: SalaryMarketFilters = {}): Promise<{
    success: boolean;
    items: SalaryMarketBucket[];
    total: number;
  }> => {
    const params: Record<string, string | number> = {};
    if (filters.role) params.role = filters.role;
    if (filters.city) params.city = filters.city;
    if (filters.experience) params.experience = filters.experience;
    if (filters.source) params.source = filters.source;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.minSamples !== undefined) params.minSamples = filters.minSamples;
    const response = await api.get('/admin/salary-market', { params });
    return response.data;
  },

  downloadCsv: async (filters: SalaryMarketFilters = {}): Promise<void> => {
    const params: Record<string, string | number> = {};
    if (filters.role) params.role = filters.role;
    if (filters.city) params.city = filters.city;
    if (filters.experience) params.experience = filters.experience;
    if (filters.source) params.source = filters.source;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.minSamples !== undefined) params.minSamples = filters.minSamples;
    const response = await api.get('/admin/salary-market/export.csv', {
      params,
      responseType: 'blob',
    });
    const today = new Date().toISOString().slice(0, 10);
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `salary-market-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

export default api;


